import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { Note } from '../types';
import { Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { FileText, Plus, Trash2, Edit2, Globe, PlusCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';

const INITIAL_NOTE: Partial<Note> = {
  title: '',
  content: '',
  is_public: false,
  tags: []
};

export default function Notes() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [notes, setNotes] = useState<Note[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Note>>(INITIAL_NOTE);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchNotes = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const { data } = await supabase.from('notes').select('*').eq('wedding_id', wedding.id);
    if (data) setNotes(data);
  };

  useEffect(() => {
    fetchNotes();
  }, [wedding]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    if (formData.id) {
      const { error } = await supabase.from('notes').update(formData).eq('id', formData.id);
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    } else {
      const { error } = await supabase.from('notes').insert({ ...formData, wedding_id: wedding.id });
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    }
    showToast('success', t('common.save'));
    setIsModalOpen(false);
    fetchNotes();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('notes').delete().eq('id', deleteId);
    setDeleteId(null);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchNotes();
  };

  if (!wedding) return null;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('nav.notes')}</h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-2">Uchovávejte si poznámky, nápady a důležité texty.</p>
        </div>
        <Button 
          onClick={() => { setFormData(INITIAL_NOTE); setIsModalOpen(true); }}
          variant="secondary"
          icon={<PlusCircle size={18} />}
        >
          {t('common.add')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {notes.length === 0 ? (
          <div className="col-span-full">
            <EmptyState 
              icon={FileText} 
              title="Žádné poznámky" 
              description="Vytvořte si první poznámku pro vaši svatbu."
              action={{ label: t('common.add'), onClick: () => { setFormData(INITIAL_NOTE); setIsModalOpen(true); } }}
            />
          </div>
        ) : (
          notes.map(note => (
            <div key={note.id} className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-ds-border transition-all group relative flex flex-col h-full">
               <div className="flex justify-between items-start mb-4">
                  {note.is_public ? (
                     <span className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 text-ds-meta px-2 py-1 rounded-lg">
                        <Globe size={12} /> Public
                     </span>
                  ) : <span />}
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                     <button onClick={() => { setFormData(note); setIsModalOpen(true); }} className="p-1.5 text-slate-400 hover:text-brand-600"><Edit2 size={16} /></button>
                     <button onClick={() => setDeleteId(note.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
               </div>
               
               <h3 className="text-ds-h3 font-semibold text-slate-900 dark:text-white mb-2">{note.title}</h3>
               <div className="flex-1">
                  <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-6 whitespace-pre-wrap leading-relaxed">{note.content}</p>
               </div>
            </div>
          ))
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? 'Upravit poznámku' : 'Nová poznámka'}>
         <form onSubmit={handleSave} className="space-y-6">
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">Název</label>
               <input 
                  type="text" 
                  required
                  value={formData.title || ''} 
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
               />
            </div>
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">Obsah</label>
               <textarea 
                  required
                  value={formData.content || ''} 
                  onChange={e => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500 custom-scrollbar"
               />
            </div>
            <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 cursor-pointer">
               <input 
                  type="checkbox" 
                  checked={formData.is_public} 
                  onChange={e => setFormData({...formData, is_public: e.target.checked})}
                  className="w-5 h-5 rounded text-brand-600 focus:ring-brand-500"
               />
               <span className="font-bold text-sm text-slate-700 dark:text-slate-300">Zveřejnit na webu svatby</span>
            </label>
            <button type="submit" className="w-full bg-brand-600 text-white font-bold py-4 rounded-xl hover:bg-brand-700 transition-colors">
               {t('common.save')}
            </button>
         </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onCancel={() => setDeleteId(null)} onConfirm={handleDelete} title={t('common.delete')} description={t('common.confirm_delete')} />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}